import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsService } from '../../services/leadsService';

// Voyager Pro reskin (Figma LeadsPage) - see AGENTS.md / memory entity
// "TPCRM Voyager Pro CRM Reskin Project" for the design system reference.
// This component is now shared by BOTH agency agents (/agent/leads) and
// independent agents (/independent/leads) - leadsService.js normalizes the
// two different backends (lead_requests vs independent_leads) behind one
// API shape so this file doesn't need to branch on user type except for a
// couple of navigation targets and field-name fallbacks noted inline.

const STATUS_CFG = {
  new: { label: 'New', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500' },
  contacted: { label: 'Contacted', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', dot: 'bg-amber-500' },
  quoted: { label: 'Quoted', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', dot: 'bg-purple-500' },
  converted: { label: 'Converted', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  lost: { label: 'Lost', color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200', dot: 'bg-slate-400' },
};

const PRIORITY_CFG = {
  urgent: { label: 'Urgent', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
  high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
  medium: { label: 'Medium', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  low: { label: 'Low', color: 'text-slate-500', bg: 'bg-slate-100', border: 'border-slate-200' },
};

const SOURCE_CFG = {
  ai_concierge: { label: 'VoyagerAI', icon: '🤖', color: 'text-violet-700', bg: 'bg-violet-50' },
  voyager_ai: { label: 'VoyagerAI', icon: '🤖', color: 'text-violet-700', bg: 'bg-violet-50' },
  website_contact: { label: 'Website', icon: '🌐', color: 'text-sky-700', bg: 'bg-sky-50' },
  website_quote_request: { label: 'Quote Request', icon: '📝', color: 'text-teal-700', bg: 'bg-teal-50' },
  wordpress_multisite: { label: 'WordPress Site', icon: '🔗', color: 'text-sky-700', bg: 'bg-sky-50' },
  content_card: { label: 'Promo Card', icon: '🎴', color: 'text-pink-700', bg: 'bg-pink-50' },
  website: { label: 'Website', icon: '🌐', color: 'text-sky-700', bg: 'bg-sky-50' },
  manual: { label: 'Manual', icon: '✍️', color: 'text-slate-600', bg: 'bg-slate-100' },
  referral: { label: 'Referral', icon: '👥', color: 'text-amber-700', bg: 'bg-amber-50' },
};

const STATUS_OPTIONS = ['new', 'contacted', 'quoted', 'converted', 'lost'];
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

const inputCls = "w-full px-3.5 py-2.5 text-sm border border-[var(--border)] rounded-xl bg-white text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold text-[var(--muted-foreground)] uppercase tracking-wider mb-1.5";

// ─── Field helpers (bridge the two different backend shapes) ──────────────────
const leadName = (l) => l.client_name || [l.first_name, l.last_name].filter(Boolean).join(' ') || 'Unknown';
const leadEmail = (l) => l.client_email || l.email;
const leadPhone = (l) => l.client_phone || l.phone;
const leadDestination = (l) => l.destination || null;
const leadPartySize = (l) => l.party_size || l.adults || 1;
const leadSourceLabel = (source) => {
  const cfg = SOURCE_CFG[source];
  return cfg ? `${cfg.icon} ${cfg.label}` : (source || 'Website');
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
const timeAgo = (d) => {
  if (!d) return '';
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
};
const formatTravelDates = (dates) => {
  if (!dates) return 'Not specified';
  if (typeof dates === 'object') {
    if (dates.start && dates.end) return `${new Date(dates.start).toLocaleDateString()} - ${new Date(dates.end).toLocaleDateString()}`;
    return dates.raw || 'Not specified';
  }
  if (typeof dates === 'string') {
    try {
      const parsed = JSON.parse(dates);
      if (parsed && typeof parsed === 'object') {
        if (parsed.start && parsed.end) return `${new Date(parsed.start).toLocaleDateString()} - ${new Date(parsed.end).toLocaleDateString()}`;
        return parsed.raw || dates;
      }
      return dates;
    } catch (e) {
      return dates;
    }
  }
  return String(dates);
};
const filterChildrenAgesFromDetails = (details) => {
  if (!details) return '';
  return String(details).split('\n').filter(line => {
    const l = line.toLowerCase();
    return !l.includes('children ages') && !l.includes('child ages') && !l.match(/ages?\s*:\s*\d/);
  }).join('\n').trim();
};

// ─── Add Lead modal ────────────────────────────────────────────────────────────
function AddLeadModal({ onSave, onClose, saving }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    destination: '', travel_dates: '', budget_range: '', party_size: 1,
    message: '', priority: 'medium',
  });
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const valid = form.first_name.trim() && form.last_name.trim() && form.email.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '92vh' }}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)] flex-shrink-0">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)]">Add Lead</h2>
            <p className="text-xs text-[var(--muted-foreground)] mt-0.5">Manually add a lead to your pipeline.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--muted)] text-[var(--muted-foreground)] transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>First Name *</label>
              <input className={inputCls} value={form.first_name} onChange={e => set('first_name', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Last Name *</label>
              <input className={inputCls} value={form.last_name} onChange={e => set('last_name', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input className={inputCls} value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Destination</label>
              <input className={inputCls} value={form.destination} onChange={e => set('destination', e.target.value)} placeholder="e.g. Italy" />
            </div>
            <div>
              <label className={labelCls}>Travel Dates</label>
              <input className={inputCls} value={form.travel_dates} onChange={e => set('travel_dates', e.target.value)} placeholder="e.g. September 2026" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Budget</label>
              <input className={inputCls} value={form.budget_range} onChange={e => set('budget_range', e.target.value)} placeholder="e.g. $5,000-$8,000" />
            </div>
            <div>
              <label className={labelCls}>Party Size</label>
              <input type="number" min="1" className={inputCls} value={form.party_size} onChange={e => set('party_size', parseInt(e.target.value) || 1)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Priority</label>
            <select className={inputCls} value={form.priority} onChange={e => set('priority', e.target.value)}>
              {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{PRIORITY_CFG[p].label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Notes / Details</label>
            <textarea rows={3} className={inputCls} value={form.message} onChange={e => set('message', e.target.value)} />
          </div>
        </div>
        <div className="flex gap-3 px-6 py-4 border-t border-[var(--border)] flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-[var(--border)] text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]/60 transition-colors">Cancel</button>
          <button
            onClick={() => valid && onSave(form)}
            disabled={!valid || saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition-all"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            {saving ? 'Adding…' : 'Add Lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Lead detail drawer ────────────────────────────────────────────────────────
function LeadDetailDrawer({ lead, onClose, onUpdateStatus, onConvert, onViewQuote, onDelete }) {
  const [notes, setNotes] = useState(lead.agent_notes || lead.notes || '');
  const [status, setStatus] = useState(lead.status);
  const [priority, setPriority] = useState(lead.priority || 'medium');
  const statusCfg = STATUS_CFG[status] || STATUS_CFG.new;
  const alreadyConverted = !!lead.quote_id || status === 'converted';

  return (
    <div className="flex flex-col h-full bg-white border-l border-[var(--border)] overflow-hidden w-full sm:w-[420px] flex-shrink-0">
      <div className="flex-shrink-0 p-5 border-b border-[var(--border)]">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <div style={{ fontFamily: 'var(--font-display)' }} className="text-xl text-[var(--foreground)] leading-tight truncate">{leadName(lead)}</div>
            <div className="text-xs text-[var(--muted-foreground)] mt-0.5 truncate">{leadEmail(lead)}</div>
            {leadPhone(lead) && <div className="text-xs text-[var(--muted-foreground)]">{leadPhone(lead)}</div>}
          </div>
          <button onClick={onClose} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex-shrink-0 p-1.5 rounded-lg hover:bg-[var(--muted)]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
            {statusCfg.label}
          </span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${PRIORITY_CFG[priority].bg} ${PRIORITY_CFG[priority].color} ${PRIORITY_CFG[priority].border}`}>
            {PRIORITY_CFG[priority].label} Priority
          </span>
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[var(--muted)] text-[var(--muted-foreground)]">
            {leadSourceLabel(lead.source)}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Trip Details</h3>
          <div className="space-y-1.5 text-sm">
            {leadDestination(lead) && (
              <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Destination</span><span className="font-medium text-[var(--foreground)]">{leadDestination(lead)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Travel Dates</span><span className="font-medium text-[var(--foreground)]">{formatTravelDates(lead.travel_dates)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Travelers</span><span className="font-medium text-[var(--foreground)]">{leadPartySize(lead)} adult{leadPartySize(lead) > 1 ? 's' : ''}{lead.children > 0 ? `, ${lead.children} child${lead.children > 1 ? 'ren' : ''}` : ''}</span></div>
            {(lead.budget_range || lead.budget) && (
              <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Budget</span><span className="font-medium text-[var(--foreground)]">{lead.budget_range || lead.budget}</span></div>
            )}
            <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Received</span><span className="font-medium text-[var(--foreground)]">{fmtDate(lead.created_at)} · {timeAgo(lead.created_at)}</span></div>
          </div>
        </div>

        {(lead.accommodation_requests || lead.activity_requests || lead.message) && (
          <div>
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Requests</h3>
            <div className="space-y-2">
              {lead.accommodation_requests && <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)]">{lead.accommodation_requests}</div>}
              {lead.activity_requests && <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)]">{lead.activity_requests}</div>}
              {lead.message && <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)]">{lead.message}</div>}
            </div>
          </div>
        )}

        {lead.additional_details && filterChildrenAgesFromDetails(lead.additional_details) && (
          <div>
            <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Additional Details</h3>
            <div className="p-3 bg-[var(--muted)]/40 rounded-xl text-sm text-[var(--foreground)] whitespace-pre-wrap">{filterChildrenAgesFromDetails(lead.additional_details)}</div>
          </div>
        )}

        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Status &amp; Priority</h3>
          <div className="grid grid-cols-2 gap-2">
            <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
            </select>
            <select value={priority} onChange={e => setPriority(e.target.value)} className={inputCls}>
              {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{PRIORITY_CFG[p].label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <h3 className="text-xs font-bold text-[var(--foreground)] uppercase tracking-widest mb-2">Agent Notes</h3>
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} className={inputCls} placeholder="Add a note about this lead…" />
        </div>
      </div>

      <div className="flex-shrink-0 p-4 border-t border-[var(--border)] space-y-2">
        <button
          onClick={() => onUpdateStatus(lead.id, status, priority, notes)}
          className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all"
          style={{ backgroundColor: 'var(--primary)' }}
        >
          Save Changes
        </button>
        {alreadyConverted ? (
          <button onClick={() => onViewQuote(lead)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--accent)' }}>
            View Quote
          </button>
        ) : (
          <button onClick={() => onConvert(lead.id)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white hover:brightness-105 transition-all" style={{ backgroundColor: 'var(--accent)' }}>
            Convert to Quote
          </button>
        )}
        <button onClick={() => onDelete(lead.id)} className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors">
          Delete Lead
        </button>
      </div>
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

const LeadsManagement = () => {
  const navigate = useNavigate();
  const isIndependent = leadsService.isIndependent();
  const quotePath = (id) => isIndependent ? `/independent/quotes/${id}/edit` : `/agent/quotes/${id}`;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ status: '', source: '' });

  const loadLeads = async () => {
    try {
      setLoading(true);
      const response = await leadsService.getAll(filters);
      setLeads(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading leads:', err);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [filters]);

  const filtered = leads.filter(l => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return leadName(l).toLowerCase().includes(term)
      || (leadEmail(l) || '').toLowerCase().includes(term)
      || (leadDestination(l) || '').toLowerCase().includes(term);
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  const handleUpdateStatus = async (id, status, priority, notes) => {
    try {
      await leadsService.updateStatus(id, { status, priority, notes });
      setSelectedLead(null);
      await loadLeads();
    } catch (err) {
      console.error('Error updating lead:', err);
      setError('Failed to update lead');
    }
  };

  const handleConvert = async (id) => {
    try {
      const response = await leadsService.convertToQuote(id);
      setSelectedLead(null);
      navigate(quotePath(response.data.quoteId), { state: { fromLeadConversion: true } });
    } catch (err) {
      console.error('Error converting lead to quote:', err);
      setError('Failed to convert lead to quote');
    }
  };

  const handleViewQuote = (lead) => {
    setSelectedLead(null);
    navigate(quotePath(lead.quote_id));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    try {
      await leadsService.delete(id);
      setSelectedLead(null);
      await loadLeads();
    } catch (err) {
      console.error('Error deleting lead:', err);
      setError('Failed to delete lead');
    }
  };

  const handleAddLead = async (form) => {
    try {
      setAddSaving(true);
      await leadsService.create(form);
      setShowAddModal(false);
      await loadLeads();
    } catch (err) {
      console.error('Error adding lead:', err);
      setError('Failed to add lead');
    } finally {
      setAddSaving(false);
    }
  };

  return (
    <div className="voyager-app flex h-full min-h-0">
      <div className={`flex-1 overflow-y-auto min-w-0 ${selectedLead ? 'hidden lg:block' : ''}`}>
        <div className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 style={{ fontFamily: 'var(--font-display)' }} className="text-3xl text-[var(--foreground)] mb-1">Leads</h1>
              <p className="text-[var(--muted-foreground)] text-sm">Track and convert incoming travel inquiries.</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-[var(--accent)] text-white rounded-xl text-sm font-semibold hover:brightness-105 transition-all shadow-sm"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
              Add Lead
            </button>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Leads', value: stats.total, icon: '📋' },
              { label: 'New', value: stats.new, icon: '🆕' },
              { label: 'Contacted', value: stats.contacted, icon: '📞' },
              { label: 'Converted', value: stats.converted, icon: '🎉' },
            ].map(s => (
              <div key={s.label} className="bg-white border border-[var(--border)] rounded-xl p-4">
                <div className="text-xl mb-1">{s.icon}</div>
                <div className="text-2xl font-bold text-[var(--primary)]">{s.value}</div>
                <div className="text-xs text-[var(--muted-foreground)] mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                type="text"
                placeholder="Search by name, email, or destination…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none transition-colors"
              />
            </div>
            <select value={filters.status} onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))} className="px-4 py-3 bg-white border border-[var(--border)] rounded-xl text-sm text-[var(--foreground)] focus:border-[var(--accent)] focus:outline-none cursor-pointer flex-shrink-0">
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_CFG[s].label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="text-center py-16 text-[var(--muted-foreground)] text-sm">Loading leads…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-[var(--border)] rounded-2xl">
              <div className="text-4xl mb-3">📋</div>
              <div className="text-sm font-medium text-[var(--foreground)] mb-1">No leads found</div>
              <div className="text-xs text-[var(--muted-foreground)]">Leads will appear here as inquiries come in, or add one manually.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(lead => {
                const statusCfg = STATUS_CFG[lead.status] || STATUS_CFG.new;
                const priorityCfg = PRIORITY_CFG[lead.priority] || PRIORITY_CFG.medium;
                const locked = !!lead.is_locked_upgrade_required;
                return (
                  <button
                    key={lead.id}
                    onClick={() => !locked && setSelectedLead(lead)}
                    className="w-full text-left bg-white border border-[var(--border)] rounded-xl p-4 hover:border-[var(--accent)] transition-colors flex items-center gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold text-sm truncate ${locked ? 'blur-sm select-none' : 'text-[var(--foreground)]'}`}>{leadName(lead)}</span>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${statusCfg.bg} ${statusCfg.color} ${statusCfg.border}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                          {statusCfg.label}
                        </span>
                        {lead.priority && lead.priority !== 'medium' && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${priorityCfg.bg} ${priorityCfg.color} ${priorityCfg.border}`}>
                            {priorityCfg.label}
                          </span>
                        )}
                      </div>
                      <div className={`text-xs text-[var(--muted-foreground)] mt-1 truncate ${locked ? 'blur-sm select-none' : ''}`}>
                        {leadEmail(lead)}{leadDestination(lead) ? ` · ${leadDestination(lead)}` : ''}
                      </div>
                    </div>
                    <div className="hidden sm:block text-xs text-[var(--muted-foreground)] flex-shrink-0">{leadSourceLabel(lead.source)}</div>
                    <div className="text-xs text-[var(--muted-foreground)] flex-shrink-0 text-right w-16">{timeAgo(lead.created_at)}</div>
                    {locked && (
                      <span
                        onClick={(e) => { e.stopPropagation(); navigate('/agent/activeagent-ai'); }}
                        className="flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[var(--accent)] text-white cursor-pointer"
                      >
                        🔒 Upgrade to Unlock
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {selectedLead && (
        <LeadDetailDrawer
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdateStatus={handleUpdateStatus}
          onConvert={handleConvert}
          onViewQuote={handleViewQuote}
          onDelete={handleDelete}
        />
      )}

      {showAddModal && (
        <AddLeadModal onSave={handleAddLead} onClose={() => setShowAddModal(false)} saving={addSaving} />
      )}
    </div>
  );
};

export default LeadsManagement;
